import is from '@sindresorhus/is';

import type { GetUsersParams } from '../../fetchers/job-architecture/job-architecture-fetchers';
import type { SearchFilters } from '../types/search-types';
import type { DebugInformationItem } from './use-debug-information';

export const jobArchitectureKeys = {
    all: ['job-architecture'] as const,
    architecture: (archId: string) => [...jobArchitectureKeys.all, 'arch', archId] as const,
    architectureDetail: (archId: string) => [...jobArchitectureKeys.all, 'architecture-detail', archId] as const,
    architectures: () => [...jobArchitectureKeys.all, 'architectures'] as const,

    archivedLevels: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'archived-levels'] as const,
    archivedRoles: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'archived-roles'] as const,
    archLevelingFramework: (archId: string, frameworkId: string) =>
        [...jobArchitectureKeys.archLevelingFrameworks(archId), frameworkId] as const,
    archLevelingFrameworks: (archId: string) =>
        [...jobArchitectureKeys.architecture(archId), 'leveling-frameworks'] as const,
    assignableLevelReviewers: (archId: string, levelId: string, searchTerm: string) =>
        [...jobArchitectureKeys.level(archId, levelId), 'assignable-reviewers', searchTerm] as const,
    assignableRoleReviewers: (archId: string, roleId: string, searchTerm: string) =>
        [...jobArchitectureKeys.role(archId, roleId), 'assignable-reviewers', searchTerm] as const,
    careerTracks: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'career-tracks'] as const,
    clusters: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'clusters'] as const,
    companyInformationGeneration: (archId: string) =>
        [...jobArchitectureKeys.architecture(archId), 'company-information-generation'] as const,
    clustersByFamily: (archId: string, familyId: string) =>
        [...jobArchitectureKeys.clusters(archId), 'by-family', familyId] as const,
    competencies: () => [...jobArchitectureKeys.all, 'competencies'] as const,
    debugInfo: (archId: string, item: DebugInformationItem) =>
        [...jobArchitectureKeys.architecture(archId), 'debug-info', item.type, item.id] as const,
    families: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'families'] as const,
    familiesWithClusters: (archId: string) => [...jobArchitectureKeys.families(archId), 'with-clusters'] as const,
    familiesRegenerationResults: (archId: string | undefined) =>
        archId === undefined
            ? ([...jobArchitectureKeys.all, 'families-regeneration-results'] as const)
            : ([...jobArchitectureKeys.architecture(archId), 'families-regeneration-results'] as const),
    family: (archId: string, familyId: string) => [...jobArchitectureKeys.families(archId), familyId] as const,
    filters: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'filters'] as const,
    history: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'history'] as const,
    inflight: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'inflight'] as const,
    itemPath: (archId: string, itemId: string) =>
        [...jobArchitectureKeys.architecture(archId), 'item-path', itemId] as const,
    level: (archId: string, levelId: string, version?: string) => {
        if (is.undefined(version)) {
            return [...jobArchitectureKeys.levels(archId), levelId] as const;
        }

        return [...jobArchitectureKeys.levels(archId), levelId, version] as const;
    },
    levelingFramework: (id: string | null) => [...jobArchitectureKeys.levelingFrameworks(), id] as const,
    levelingFrameworks: () => [...jobArchitectureKeys.all, 'leveling-frameworks'] as const,
    levels: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'levels'] as const,
    levelsByRole: (archId: string, roleId: string) =>
        [...jobArchitectureKeys.levels(archId), 'by-role', roleId] as const,
    levelVersions: (archId: string, levelId: string) =>
        [...jobArchitectureKeys.level(archId, levelId), 'versions'] as const,
    metadata: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'metadata'] as const,
    onboardingState: (archId: string | null) =>
        archId === null
            ? ([...jobArchitectureKeys.all, 'onboarding-state'] as const)
            : ([...jobArchitectureKeys.architecture(archId), 'onboarding-state'] as const),
    options: () => [...jobArchitectureKeys.all, 'options'] as const,
    regenerationResult: (archId: string, regenerationId: string) =>
        [...jobArchitectureKeys.architecture(archId), 'regeneration-result', regenerationId] as const,
    profile: (archId: string, profileId: string) => [...jobArchitectureKeys.profiles(archId), profileId] as const,
    profiles: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'profiles'] as const,
    relatedSkills: (skills: readonly { readonly id: string }[], acceptLanguage: string) =>
        [
            ...jobArchitectureKeys.all,
            'skills',
            'related',
            skills
                .map((s) => s.id)
                .sort()
                .join(','),
            acceptLanguage,
        ] as const,
    role: (archId: string, roleId: string, version?: string) => {
        if (is.undefined(version)) {
            return [...jobArchitectureKeys.roles(archId), roleId] as const;
        }

        return [...jobArchitectureKeys.roles(archId), roleId, version] as const;
    },
    roles: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'roles'] as const,
    rolesAndLevels: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'roles-and-levels'] as const,
    rolesAndLevelsByCluster: (archId: string, clusterId: string) =>
        [...jobArchitectureKeys.rolesAndLevels(archId), 'by-cluster', clusterId] as const,
    rolesByCluster: (archId: string, clusterId: string) =>
        [...jobArchitectureKeys.roles(archId), 'by-cluster', clusterId] as const,
    roleVersions: (archId: string, roleId: string) =>
        [...jobArchitectureKeys.role(archId, roleId), 'versions'] as const,
    search: (archId: string, filters: SearchFilters) =>
        [...jobArchitectureKeys.architecture(archId), 'search', filters] as const,
    skillCatalog: () => ['skill-catalog'] as const,
    skillCatalogOptions: () => ['skill-catalog', 'options'] as const,
    skillCatalogStatistics: () => ['skill-catalog', 'statistics'] as const,
    skillDetails: (skillId: string, language: string) => ['skill-details', skillId, language] as const,
    skillLimits: (archId: string) => [...jobArchitectureKeys.architecture(archId), 'skill-limits'] as const,
    skillLocales: () => [...jobArchitectureKeys.all, 'skill-locales'] as const,
    skillsBySearchTerm: (searchTerm: string, acceptLanguage: string, sort?: string) =>
        [...jobArchitectureKeys.all, 'skills', 'search', searchTerm, acceptLanguage, sort] as const,
    skillsCatalogSearch: (
        searchTerm: string,
        acceptLanguage: string,
        sort?: string,
        statusFilter?: readonly string[],
        roleIds?: readonly string[],
        levelIds?: readonly string[],
        skillTypes?: readonly string[],
        sources?: readonly string[],
        jobArchitectureIds?: readonly string[],
    ) =>
        [
            ...jobArchitectureKeys.skillCatalog(),
            'search',
            searchTerm,
            acceptLanguage,
            sort,
            statusFilter,
            roleIds,
            levelIds,
            skillTypes,
            sources,
            jobArchitectureIds,
        ] as const,
    users: (params: GetUsersParams) => [...jobArchitectureKeys.all, 'users', params] as const,
} as const;
